/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { call, delay, takeLatest } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import { ConsumptionMeta, Seconds, ActionRequest, ActionSuccess } from '@wit/vtv-x-foundation-lib';

export enum ActionTypes {
  START = 'consumption/delay/START',
  END = 'consumption/delay/END',
  CANCEL = 'consumption/delay/CANCEL',
}

type DelayedFn = (meta: ConsumptionMeta) => any;

interface MetaDelay {
  time: Seconds;
  fn: DelayedFn;
  meta: ConsumptionMeta;
}

type Start = ActionRequest<ActionTypes.START, MetaDelay>;
type End = ActionSuccess<ActionTypes.END, MetaDelay>;
type Cancel = ActionRequest<ActionTypes.CANCEL>;

type Actions = Start | End | Cancel;

const Actions = {
  start: (meta: MetaDelay): Start => ({ type: ActionTypes.START, meta }),
  end: (payload: MetaDelay): End => ({ type: ActionTypes.END, payload }),
  cancel: (): Cancel => ({ type: ActionTypes.CANCEL }),
};

export const Creators = {
  delay: (time: Seconds, fn: DelayedFn, meta: ConsumptionMeta): Start => Actions.start({ time, fn, meta }),
  cancel: (): Cancel => Actions.cancel(),
};

// REDUCERS
export interface State {
  data?: ConsumptionMeta;
  waiting: boolean;
  cancelled: boolean;
}

const initialState: State = {
  waiting: false,
  cancelled: false,
};

export default function programsReducer(state = initialState, action: Actions): State {
  switch (action.type) {
    case ActionTypes.START: {
      return {
        data: action.meta.meta,
        waiting: true,
        cancelled: false,
      };
    }
    case ActionTypes.END:
      return {
        waiting: false,
        cancelled: false,
      };

    case ActionTypes.CANCEL:
      if (state.waiting) {
        return {
          ...state,
          waiting: false,
          cancelled: true,
        };
      }
      return state;
    default:
      return state;
  }
}

// SAGAS
function* handleInput(action: Actions): SagaIterator {
  // If action is CANCEL it will cancel the latest action with the takeLatest
  if (action.type === ActionTypes.START) {
    yield delay(action.meta.time * 1000);
    yield call(action.meta.fn, action.meta.meta);
  }
}

export function* saga(): SagaIterator {
  yield takeLatest([ActionTypes.START, ActionTypes.CANCEL], handleInput);
}
